import semver from 'semver';

import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { getAction } from '../actions/getters';
import { getAllAccountConnections } from '../credentials/getters';
import AppActionService from '@/utils/AppActionService';

const buildAppGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const getAllServices = buildAppGetterObject('getAllServices');
export const getAllServicesList = buildAppGetterObject('getAllServicesList');
export const getAllActiveServicesList = buildAppGetterObject('getAllActiveServicesList');
export const getService = buildAppGetterObject('getService');
export const getServiceBySlug = buildAppGetterObject('getServiceBySlug');
export const servicesActionsIds = buildAppGetterObject('servicesActionsIds');
export const servicesSearchOrCreatesActionsIds = buildAppGetterObject('servicesSearchOrCreatesActionsIds');
export const servicesAllActionsIds = buildAppGetterObject('servicesAllActionsIds');
export const servicesUserCredentials = buildAppGetterObject('servicesUserCredentials');
export const getCredentialsForService = buildAppGetterObject('getCredentialsForService');
export const getCredentialsForServiceMinorVersion = buildAppGetterObject('getCredentialsForServiceMinorVersion');
export const getServiceActionsIds = buildAppGetterObject('getServiceActionsIds');
export const getServiceSearchOrCreatesActionsIds = buildAppGetterObject('getServiceSearchOrCreatesActionsIds');
export const getServiceAllActionsIds = buildAppGetterObject('getServiceAllActionsIds');
export const getServiceAllActions = buildAppGetterObject('getServiceAllActions');
export const isActionSearchOrCreateOfService = buildAppGetterObject('isActionSearchOrCreateOfService');
export const getActiveFetchOfActionsForServiceVersion = buildAppGetterObject('getActiveFetchOfActionsForServiceVersion');
export const getServiceActionsByVersion = buildAppGetterObject('getServiceActionsByVersion');
export const getServiceActionCollection = buildAppGetterObject('getServiceActionCollection');
export const getServiceActionCollectionBySlug = buildAppGetterObject('getServiceActionCollectionBySlug');
export const getAllServiceActionCollections = buildAppGetterObject('getAllServiceActionCollections');
export const getServiceActionCollectionActions = buildAppGetterObject('getServiceActionCollectionActions');
export const useDirectoryApp = buildAppGetterObject('useDirectoryApp');
export const useAllDirectoryApps = buildAppGetterObject('useAllDirectoryApps');
export const useAllFetchedDirectoryAppsIds = buildAppGetterObject('useAllFetchedDirectoryAppsIds');

const getters = {
	[getAllServices.getter]: (state) => state.data,
	[useDirectoryApp.getter]: (state, getters) => (appId) => getters[useAllDirectoryApps.getter][appId],
	[useAllDirectoryApps.getter]: (state) => state.directoryApps,
	[useAllFetchedDirectoryAppsIds.getter]: (state, getters) => Object.keys(getters[useAllDirectoryApps.getter]),
	[getAllServicesList.getter]: (state, getters) => Object.values(getters[getAllServices.getter]),
	[getAllActiveServicesList.getter]: (state, getters) => getters[getAllServicesList.getter].filter((service) => !service.hidden),
	[getService.getter]: (state) => (serviceId) => state.data[serviceId],
	[getServiceBySlug.getter]: (state, getters) => (serviceSlug) => {
		const servicesArray = getters[getAllServicesList.getter];
		return servicesArray.find((service) => service.service_slug === serviceSlug);
	},
	[servicesActionsIds.getter]: (state) => state.servicesActionsIds,
	[servicesSearchOrCreatesActionsIds.getter]: (state) => state.servicesSearchOrCreatesActionsIds,
	[servicesAllActionsIds.getter]: (state, getters) => {
		const servicesIds = Object.keys(state.data);
		const servicesAllActions = {};
		servicesIds.forEach((serviceId) => {
			servicesAllActions[serviceId] = {
				actions: getters[getServiceActionsIds.getter](serviceId) || [],
				searchOrCreates: getters[getServiceSearchOrCreatesActionsIds.getter](serviceId) || [],
			};
		});
		return servicesAllActions;
	},
	[servicesUserCredentials.getter]: (state, getters, rootState, rootGetters) => {
		const profiles = Object.values(rootGetters[getAllAccountConnections.fullPath]);
		return profiles.reduce((servicesToCredentials, profile) => {
			const { service } = profile;
			if (!(service in servicesToCredentials)) {
				servicesToCredentials[service] = [];
			}
			servicesToCredentials[service].push(profile);
			return servicesToCredentials;
		}, {});
	},
	[getCredentialsForService.getter]: (state, getters) => (serviceId) => getters[servicesUserCredentials.getter][serviceId],
	[getCredentialsForServiceMinorVersion.getter]: (state, getters) => (serviceId, serviceVersion) => {
		const majorVersion = semver.major(serviceVersion);
		return getters[getCredentialsForService.getter](serviceId)?.filter((creds) =>
			semver.satisfies(creds.service_version, `${majorVersion}.x`)
		);
	},
	[getServiceActionsIds.getter]: (state, getters) => (serviceId) => getters[servicesActionsIds.getter][serviceId],
	[getServiceSearchOrCreatesActionsIds.getter]: (state, getters) => (serviceId) =>
		getters[servicesSearchOrCreatesActionsIds.getter][serviceId],
	[getServiceAllActionsIds.getter]: (state, getters) => (serviceId) => getters[servicesAllActionsIds.getter][serviceId],
	[getServiceAllActions.getter]: (state, getters, rootState, rootGetters) => (serviceId) => {
		const appTotalActionsIds = { ...getters[getServiceAllActionsIds.getter](serviceId) };
		Object.assign(appTotalActionsIds, {
			actions: [...(appTotalActionsIds?.actions || [])].map((actionId) => rootGetters[getAction.fullPath](actionId)),
		});
		Object.assign(appTotalActionsIds, {
			searchOrCreates: [...(appTotalActionsIds.searchOrCreates || [])].map((actionId) => rootGetters[getAction.fullPath](actionId)),
		});
		return appTotalActionsIds;
	},
	[isActionSearchOrCreateOfService.getter]: (state, getters) => (serviceId, actionId) => {
		const searchOrCreateActionsIds = getters[getServiceSearchOrCreatesActionsIds.getter](serviceId);
		const isSearchOrCreateAction = searchOrCreateActionsIds && searchOrCreateActionsIds.includes(actionId);
		return isSearchOrCreateAction;
	},
	[getActiveFetchOfActionsForServiceVersion.getter]: (state) => (fetchKey) => state.activeAppActionsFetches[fetchKey],
	[getServiceActionsByVersion.getter]: (state, getters, rootState, rootGetters) => (serviceId, version) =>
		(state.actionIdsByVersion?.[serviceId]?.[version] || [])
			.map(rootGetters[getAction.fullPath])
			.filter((action) => !AppActionService.doesActionHasServiceActionCollection(action)),
	[getAllServiceActionCollections.getter]: (state) => Object.values(state.collectionActionsData),
	[getServiceActionCollection.getter]: (state) => (serviceActionCollectionId) => state.collectionActionsData[serviceActionCollectionId],
	[getServiceActionCollectionBySlug.getter]: (state) => (serviceSlug) =>
		Object.values(state.collectionActionsData).find((sac) => sac.service_slug === serviceSlug),
	[getServiceActionCollectionActions.getter]: (state, getters, rootState, rootGetters) => (serviceActionCollectionId) =>
		getters[getServiceActionCollection.getter](serviceActionCollectionId)
			.actions.map((actionId) => rootGetters[getAction.fullPath](actionId))
			.filter((action) => !!action),
};

export default getters;
